// Zod schema for agent file frontmatter.
// This is the single source of truth for validation rules per editor spec §8.
// Used by FrontmatterForm.tsx for inline validation and can be imported for
// server-side pre-validation before calling the save RPC.

import { z } from "zod";

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]*$/;

const triggerRuleSchema = z.object({
  field: z.string().min(1, "Field name is required"),
  op: z.enum(["equals", "contains", "gt", "lt", "regex"], {
    errorMap: () => ({ message: "Operator must be one of: equals, contains, gt, lt, regex" }),
  }),
  value: z.string().min(1, "Value is required"),
});

// Schema for an Agent (kind === 'agent') — no trigger fields.
const agentFrontmatterSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .regex(SLUG_REGEX, "Must be a valid slug (e.g. utility-vendors)"),
  description: z
    .string()
    .min(1, "Description is required")
    .max(500, "Description must be 500 characters or fewer"),
});

// Base skill schema (without superRefine so .extend() works on ZodObject).
const skillFrontmatterBaseSchema = agentFrontmatterSchema.extend({
  trigger_type: z.enum(["description", "rule"], {
    errorMap: () => ({ message: "Trigger type must be description or rule" }),
  }),
  trigger_rule: z.union([triggerRuleSchema, z.null()]).optional().nullable(),
  // Ontology spec §10: optional Object Type binding.
  object_type_api_name: z.string().min(1).optional().nullable(),
});

// Schema for a Skill or Sub-skill — requires trigger_type and conditional trigger_rule.
const skillFrontmatterSchema = skillFrontmatterBaseSchema.superRefine((data, ctx) => {
  if (data.trigger_type === "rule") {
    if (!data.trigger_rule || typeof data.trigger_rule !== "object") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["trigger_rule"],
        message: "Trigger rule is required when trigger type is Rule",
      });
    }
  }
});

export { agentFrontmatterSchema, skillFrontmatterSchema, triggerRuleSchema };
export type AgentFrontmatterInput = z.infer<typeof agentFrontmatterSchema>;
export type SkillFrontmatterInput = z.infer<typeof skillFrontmatterSchema>;
